// src/features/procedures/components/StepDescription.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Collapse, Typography } from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MenuBook as GuideIcon,
} from '@mui/icons-material';

// Guidance shorter than this is shown inline; hiding it would cost a click for nothing
const COLLAPSE_THRESHOLD = 160;

/**
 * Splits a step description into its first paragraph (the question, always
 * visible) and the rest (guidance, collapsed behind "Vis vejledning" when long).
 */
export function splitDescription(description) {
  const text = (description || '').trim();
  const breakAt = text.search(/\n\s*\n/);
  if (breakAt === -1) return { summary: text, guidance: '' };
  return {
    summary: text.slice(0, breakAt).trim(),
    guidance: text.slice(breakAt).trim(),
  };
}

export default function StepDescription({ description, variant = 'body2', defaultOpen = false }) {
  const { t } = useTranslation(['procedures']);
  const [open, setOpen] = useState(defaultOpen);
  const { summary, guidance } = splitDescription(description);

  if (!summary) return null;

  const collapsible = guidance.length > COLLAPSE_THRESHOLD;

  return (
    <Box>
      <Typography variant={variant} color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
        {summary}
      </Typography>

      {guidance && !collapsible && (
        <Typography variant={variant} color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
          {guidance}
        </Typography>
      )}

      {collapsible && (
        <>
          <Button
            size="small"
            onClick={() => setOpen((prev) => !prev)}
            startIcon={<GuideIcon fontSize="small" />}
            endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ mt: 0.5, ml: -0.75, px: 0.75, textTransform: 'none' }}
          >
            {open
              ? t('procedures:guidance.hide', 'Skjul vejledning')
              : t('procedures:guidance.show', 'Vis vejledning')}
          </Button>
          <Collapse in={open} unmountOnExit>
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                borderLeft: 3,
                borderColor: 'primary.light',
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Typography variant={variant} color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {guidance}
              </Typography>
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  );
}
